/**
 * StagePanel — generic wrapper for pipeline stage UI.
 *
 * Handles the common pattern: run button, progress indicator, LLM artifacts
 * (prompt, raw output, model settings), debug panel, output display.
 *
 * Reads from ArticleContext — no props needed for document, run, or job state.
 */

import React, { useCallback, useState } from 'react';
import { useArticleContext } from './ArticleContext';
import { RunProgress } from './shared/RunProgress';
import { useArtifact } from './shared/useArtifact';
import { StagePanelDetails } from './StagePanelDetails';
import type { ArticleDocument } from './types';
import type { JobRecord } from '../../services/jobsService';
import { blogRunsService } from '../../services/blogRunsService';

type StageJobState = {
  running: boolean;
  jobStatus: string | null;
  jobProgress: any[];
  jobError: any;
  jobId: string | null;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface StagePanelProps {
  /** The stage ID (e.g., 'stage_1a_keywords'). */
  stageId: string;
  /** Display name for the stage. */
  title: string;
  /** Short description of what this stage does. */
  description?: string;
  /**
   * Custom content rendered above the run button.
   * Use for inputs, configuration, or stage-specific UI.
   */
  children?: React.ReactNode;
  /**
   * Custom output rendering. Receives the current document.
   * If not provided, output section is not shown.
   */
  renderOutput?: (document: ArticleDocument) => React.ReactNode;
  /**
   * Optional live-progress renderer, shown between the run button and the run
   * status box while a run is in flight. Receives the job's progress events and
   * whether a run is active. Use for stage-specific live feedback.
   */
  renderLiveProgress?: (progress: any[], running: boolean) => React.ReactNode;
  /**
   * If true, the run button is disabled regardless of other conditions.
   * Use for stages that need prerequisite data.
   */
  runDisabled?: boolean;
  /** Tooltip text when run button is disabled. */
  runDisabledReason?: string;
  /** Override the run button label. Default: "Run {title}". */
  runLabel?: string;
  /** If true, don't show the run button at all (e.g., human-only stages). */
  hideRunButton?: boolean;
  /**
   * If true, show the prompt editor (template editing, preview, model settings)
   * AND the LLM artifact panels (last prompt sent, raw output).
   * Set this for stages that call an LLM (REQUIRES_LLM = True on the backend).
   */
  isLlmStage?: boolean;
  /**
   * If true, show only the prompt editor (no artifact panels).
   * Use for non-LLM stages that make internal LLM calls via io.call_llm —
   * they have editable prompts in DynamoDB but the worker doesn't save
   * per-stage debug artifacts to S3.
   */
  showPromptEditor?: boolean;
  /**
   * For fan-out stages: the currently selected extraction ID. When set,
   * artifact loading (prompt, raw output) will include this ID so the
   * correct per-extraction artifacts are fetched from S3.
   */
  extractionId?: string;
  /**
   * Build a custom payload to pass to executeStage. Called when the user
   * clicks Run. Use for stages that need ephemeral inputs (CSV data, etc.)
   * in the job payload. StagePanel manages all job state internally.
   */
  buildPayload?: () => Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function StagePanel({
  stageId,
  title,
  description,
  children,
  renderOutput,
  renderLiveProgress,
  runDisabled,
  runDisabledReason,
  runLabel,
  hideRunButton,
  isLlmStage,
  showPromptEditor,
  extractionId,
  buildPayload,
}: StagePanelProps) {
  const { document, run, executeStage, refreshDocument, loading: docLoading, isAnyJobActive, activeJob } = useArticleContext();
  const runId = run?.run_id;

  // Rerun confirmation dialog
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);

  // Local job state for stages using context's executeStage
  const [running, setRunning] = useState(false);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<any[]>([]);
  const [jobError, setJobError] = useState<any>(null);

  const activeJobState: StageJobState = {
    running,
    jobStatus,
    jobProgress,
    jobError,
    jobId: null,
  };

  // Lazy-loaded artifacts from S3 (only for LLM stages)
  const prompt = useArtifact(runId, stageId, blogRunsService.getStagePrompt, !!isLlmStage, extractionId);
  const rawOutput = useArtifact(runId, stageId, blogRunsService.getStageRaw, !!isLlmStage, extractionId);

  // Check if this stage has already completed
  const isStageCompleted = (run?.stages || []).some(
    (s: { stage_id: string; status: string }) => s.stage_id === stageId && s.status === 'succeeded'
  );

  const handleRunConfirmed = useCallback(async () => {
    setRunning(true);
    setJobStatus('queued');
    setJobProgress([]);
    setJobError(null);
    try {
      const payload = buildPayload ? buildPayload() : undefined;
      await executeStage(stageId, {
        payload,
        onUpdate: (job: JobRecord) => {
          setJobStatus(job.status || null);
          if (Array.isArray(job.progress)) {
            setJobProgress(job.progress);
          }
        },
      });
      setJobStatus('succeeded');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Stage execution failed';
      setJobError(msg);
      setJobStatus('failed');
    } finally {
      setRunning(false);
    }
  }, [stageId, executeStage, buildPayload]);

  const handleRunClick = useCallback(() => {
    if (isStageCompleted) {
      setShowRerunConfirm(true);
    } else {
      handleRunConfirmed();
    }
  }, [isStageCompleted, handleRunConfirmed]);

  const handleRerunFromStage = useCallback(async () => {
    if (!runId) return;
    setShowRerunConfirm(false);
    setRunning(true);
    setJobStatus('resetting');
    try {
      await blogRunsService.resetFromStage(runId, stageId);
      await refreshDocument();
      setJobStatus('queued');
      const payload = buildPayload ? buildPayload() : undefined;
      await executeStage(stageId, {
        payload,
        onUpdate: (job: JobRecord) => {
          setJobStatus(job.status || null);
          if (Array.isArray(job.progress)) {
            setJobProgress(job.progress);
          }
        },
      });
      setJobStatus('succeeded');
    } catch (e: any) {
      setJobError(e?.message || 'Rerun failed');
      setJobStatus('failed');
    } finally {
      setRunning(false);
    }
  }, [runId, stageId, executeStage, buildPayload, refreshDocument]);

  const isRunning = activeJobState.running;
  // Block execution when another job (from this or another session) is active
  const externalJobRunning = isAnyJobActive && !isRunning;
  const isDisabled = runDisabled || isRunning || docLoading || externalJobRunning;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* External job running banner */}
      {externalJobRunning && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-800">
          A job is currently running for this article. Please wait for it to complete before starting a new one.
        </div>
      )}

      {/* Custom content (inputs, configuration, etc.) */}
      {children}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        {!hideRunButton && (
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#1a2332' }}
            disabled={isDisabled}
            title={isDisabled ? (externalJobRunning ? 'Another job is running for this article' : runDisabledReason || (isRunning ? 'Running...' : '')) : undefined}
            onClick={handleRunClick}
          >
            {isRunning ? 'Running...' : externalJobRunning ? 'Job in progress...' : (runLabel || `Run ${title}`)}
          </button>
        )}
        {!hideRunButton && activeJobState.jobStatus && activeJobState.jobStatus !== 'succeeded' && activeJobState.jobStatus !== 'failed' && (
          <span className="text-xs text-gray-500">{activeJobState.jobStatus}</span>
        )}
      </div>

      {/* Live activity (stage-specific, e.g. the strategist's thinking stream) */}
      {renderLiveProgress && renderLiveProgress(activeJobState.jobProgress, isRunning)}

      {/* Progress */}
      {(isRunning || activeJobState.jobProgress.length > 0) && (
        <RunProgress
          jobStatus={activeJobState.jobStatus}
          jobProgress={activeJobState.jobProgress}
        />
      )}

      {/* Error */}
      {activeJobState.jobError && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
          {typeof activeJobState.jobError === 'string'
            ? activeJobState.jobError
            : JSON.stringify(activeJobState.jobError)}
        </div>
      )}

      {/* Output */}
      {document && renderOutput && (
        <div className="border-t border-gray-200 pt-4">
          {renderOutput(document)}
        </div>
      )}

      {/* Details (collapsed by default) */}
      <StagePanelDetails
        stageId={stageId}
        title={title}
        runId={runId}
        isLlmStage={isLlmStage}
        showPromptEditor={showPromptEditor}
        isStageCompleted={isStageCompleted}
        prompt={prompt}
        rawOutput={rawOutput}
        jobStatus={activeJobState.jobStatus}
        jobError={activeJobState.jobError}
        jobProgress={activeJobState.jobProgress}
      />

      {/* Rerun confirmation dialog */}
      {showRerunConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/40" onClick={() => setShowRerunConfirm(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5 space-y-4 border border-gray-200" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-gray-900">Rerun this stage?</h3>
            <p className="text-sm text-gray-600">
              This will clear all progress from <strong>{title}</strong> onward. All downstream stages will need to run again.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm border rounded-md border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowRerunConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90"
                style={{ backgroundColor: '#1a2332' }}
                onClick={handleRerunFromStage}
              >
                Rerun
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
